import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import {
  useDeleteGoogleDriveSource,
  useDeleteMicrosoftTenantSource,
  useDriveSources,
  useDrives,
  useGoogleDriveSources,
  useMemberExternalAccounts,
  useMicrosoftTenantSources,
  useRelays,
  useRevokeRelay,
  useStartXeroHubConnect,
  useXeroConnection,
} from '@/hooks';
import {
  useMcpServerConnections,
  usePinMcpServerConnection,
  useProbeMcpServerConnection,
} from '@/hooks/useMcpServerConnectionQueries';
import { getAxiosErrorDisplayMessage } from '@/utils/error-utils';
import type { DriveSafe } from '@vivport/backend/types';
import { Loader2 } from 'lucide-react';
import { useIsMutating } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { DrivesSection } from '../DrivesSection';
import { DriveRootsPanel } from '../drives/DriveRootsPanel';
import { DrivesReadOnlyList } from '../drives/DrivesReadOnlyList';
import { RelayDetails } from '../drives/RelayDetails';
import { McpConnectionCard } from '../mcp/McpConnectionCard';
import { McpConnectionDeleteDialog } from '../mcp/McpConnectionDeleteDialog';
import { McpConnectionFormDialog } from '../mcp/McpConnectionFormDialog';
import { describePinResult, type McpProbeState } from '../mcp/mcp-connection-pin-result';
import { ConfirmActionDialog } from './ConfirmActionDialog';
import { GoogleDriveSourceDetails } from './GoogleDriveSourceDetails';
import { IntegrationPageHeader } from './IntegrationPageHeader';
import { MemberExternalAccountCard } from './MemberExternalAccountCard';
import { MicrosoftTenantSourceDetails } from './MicrosoftTenantSourceDetails';
import { XeroConnectionBody } from './XeroConnectionBody';
import type { IntegrationKind } from './integration-kinds';
import {
  IntegrationRow,
  rowsFromDriveSources,
  rowsFromMcpConnections,
  rowsFromMemberExternalAccounts,
  rowsFromXeroConnection,
} from './integration-rows';
import { startConnectInNewTab } from './start-connect-in-new-tab';

const INDEX_PATH = '/settings/integrations';

type InstanceProps = {
  id: number;
  memberId: number;
  isHubAdmin: boolean;
};

type IntegrationInstancePageProps = InstanceProps & {
  kind: IntegrationKind;
};

function InstanceLoading() {
  return <p className="text-sm text-muted-foreground">Loading...</p>;
}

function InstanceGone() {
  const isMutating = useIsMutating();
  const [removedHere] = useState(isMutating > 0);
  useEffect(() => {
    if (!removedHere) {
      toast.error('That integration no longer exists.', { id: 'integration-gone' });
    }
  }, [removedHere]);
  return <Navigate to={INDEX_PATH} replace />;
}

function InstanceError({ error }: { error: unknown }) {
  return <p className="text-sm text-destructive">{getAxiosErrorDisplayMessage(error, 'Failed to load this integration.')}</p>;
}

function AdminOnlyNote() {
  return <p className="text-sm text-muted-foreground">Hub admins manage this integration.</p>;
}

function InstanceLayout({ kind, row, actions, children }: { kind: IntegrationKind; row: IntegrationRow; actions?: ReactNode; children: ReactNode }) {
  return (
    <div className="space-y-6">
      <IntegrationPageHeader kind={kind} name={row.name} status={row.status} actions={actions} />
      {children}
    </div>
  );
}

function useLeave(): () => void {
  const navigate = useNavigate();
  return () => navigate(INDEX_PATH, { replace: true });
}

function AccountInstance({ kind, id, memberId }: InstanceProps & { kind: 'google-account' | 'microsoft-account' }) {
  const accounts = useMemberExternalAccounts(memberId);
  const leave = useLeave();
  if (accounts.isLoading) {
    return <InstanceLoading />;
  }
  if (accounts.isError) {
    return <InstanceError error={accounts.error} />;
  }
  const account = (accounts.data?.items ?? []).find((item) => item.id === id);
  const row = account ? rowsFromMemberExternalAccounts([account]).find((candidate) => candidate.kind === kind) : undefined;
  if (!account || !row) {
    return <InstanceGone />;
  }
  return (
    <InstanceLayout kind={kind} row={row}>
      <MemberExternalAccountCard account={account} memberId={memberId} onDisconnected={leave} />
    </InstanceLayout>
  );
}

function useDriveSourceRow(kind: 'google-drive' | 'microsoft-tenant' | 'relay', id: number, memberId: number) {
  const summaries = useDriveSources(memberId);
  const summary = (summaries.data?.sources ?? []).find((source) => source.id === id);
  const row = summary ? rowsFromDriveSources([summary]).find((candidate) => candidate.kind === kind) : undefined;
  return { row, isLoading: summaries.isLoading, isError: summaries.isError, error: summaries.error };
}

function GoogleDriveInstance({ id, memberId, isHubAdmin }: InstanceProps) {
  const sources = useGoogleDriveSources(memberId);
  const status = useDriveSourceRow('google-drive', id, memberId);
  const drives = useDrives(memberId);
  const deleteSource = useDeleteGoogleDriveSource();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const leave = useLeave();

  if (sources.isLoading || status.isLoading) {
    return <InstanceLoading />;
  }
  if (sources.isError || status.isError) {
    return <InstanceError error={sources.error ?? status.error} />;
  }
  const source = (sources.data?.sources ?? []).find((item) => item.id === id);
  if (!source || !status.row) {
    return <InstanceGone />;
  }

  const handleRemove = () => {
    deleteSource.mutate(source.id, {
      onSuccess: () => {
        toast.success(`Disconnected ${source.name}.`);
        leave();
      },
      onError: (error: unknown) => {
        toast.error(getAxiosErrorDisplayMessage(error, 'Failed to disconnect Google Drive.'));
      },
    });
  };

  return (
    <InstanceLayout
      kind="google-drive"
      row={status.row}
      actions={
        isHubAdmin ? (
          <Button type="button" variant="destructive" onClick={() => setConfirmOpen(true)} disabled={deleteSource.isPending}>
            {deleteSource.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Remove
          </Button>
        ) : null
      }
    >
      <GoogleDriveSourceDetails source={source} isHubAdmin={isHubAdmin} />
      {isHubAdmin ? (
        <DrivesSection memberId={memberId} sourceId={id} />
      ) : (
        <>
          <DrivesReadOnlyList drives={(drives.data?.drives ?? []).filter((drive) => drive.sourceId === id)} />
          <AdminOnlyNote />
        </>
      )}
      <ConfirmActionDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Remove Google Drive"
        description={`Disconnect ${source.name}? Remove its drives first if any remain.`}
        confirmLabel="Remove"
        onConfirm={handleRemove}
      />
    </InstanceLayout>
  );
}

function MicrosoftTenantInstance({ id, memberId, isHubAdmin }: InstanceProps) {
  const sources = useMicrosoftTenantSources(memberId);
  const status = useDriveSourceRow('microsoft-tenant', id, memberId);
  const drives = useDrives(memberId);
  const deleteSource = useDeleteMicrosoftTenantSource();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const leave = useLeave();

  if (sources.isLoading || status.isLoading) {
    return <InstanceLoading />;
  }
  if (sources.isError || status.isError) {
    return <InstanceError error={sources.error ?? status.error} />;
  }
  const source = (sources.data?.sources ?? []).find((item) => item.id === id);
  if (!source || !status.row) {
    return <InstanceGone />;
  }

  const handleRemove = () => {
    deleteSource.mutate(source.id, {
      onSuccess: (result) => {
        toast.success(result.note);
        leave();
      },
      onError: (error: unknown) => {
        toast.error(getAxiosErrorDisplayMessage(error, 'Failed to remove the tenant link.'));
      },
    });
  };

  return (
    <InstanceLayout
      kind="microsoft-tenant"
      row={status.row}
      actions={
        isHubAdmin ? (
          <Button type="button" variant="destructive" onClick={() => setConfirmOpen(true)} disabled={deleteSource.isPending}>
            {deleteSource.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Remove
          </Button>
        ) : null
      }
    >
      <MicrosoftTenantSourceDetails
        source={source}
        applicationClientId={sources.data?.applicationClientId ?? ''}
        isHubAdmin={isHubAdmin}
      />
      {isHubAdmin ? (
        <DrivesSection memberId={memberId} sourceId={id} />
      ) : (
        <>
          <DrivesReadOnlyList drives={(drives.data?.drives ?? []).filter((drive) => drive.sourceId === id)} />
          <AdminOnlyNote />
        </>
      )}
      <ConfirmActionDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Remove SharePoint tenant"
        description={`Remove ${source.name} from this Hub? Consent itself stays in Azure until the tenant admin removes the enterprise application. Remove its drives first if any remain.`}
        confirmLabel="Remove"
        onConfirm={handleRemove}
      />
    </InstanceLayout>
  );
}

function RelayFolderCard({ drive, isHubAdmin }: { drive: DriveSafe; isHubAdmin: boolean }) {
  return (
    <Card>
      <CardHeader className="space-y-1">
        <span className="text-base font-medium">{drive.name}</span>
        <p className="text-sm text-muted-foreground">{drive.productLabel}</p>
      </CardHeader>
      {isHubAdmin ? (
        <CardContent>
          <DriveRootsPanel drive={drive} />
        </CardContent>
      ) : null}
    </Card>
  );
}

function RelayInstance({ id, memberId, isHubAdmin }: InstanceProps) {
  const relays = useRelays(memberId);
  const status = useDriveSourceRow('relay', id, memberId);
  const drives = useDrives(memberId);
  const revoke = useRevokeRelay();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const leave = useLeave();

  if (relays.isLoading || status.isLoading || drives.isLoading) {
    return <InstanceLoading />;
  }
  if (relays.isError || status.isError || drives.isError) {
    return <InstanceError error={relays.error ?? status.error ?? drives.error} />;
  }
  const relay = (relays.data?.relays ?? []).find((item) => item.id === id);
  if (!relay || !status.row) {
    return <InstanceGone />;
  }
  const folders = (drives.data?.drives ?? []).filter((drive) => drive.sourceId === id);

  const handleRevoke = () => {
    revoke.mutate(relay.id, {
      onSuccess: () => {
        toast.success(`Revoked relay '${relay.name}'.`);
        leave();
      },
      onError: (error: unknown) => {
        toast.error(getAxiosErrorDisplayMessage(error, 'Failed to revoke the relay.'));
      },
    });
  };

  return (
    <InstanceLayout
      kind="relay"
      row={status.row}
      actions={
        isHubAdmin ? (
          <Button type="button" variant="destructive" onClick={() => setConfirmOpen(true)} disabled={revoke.isPending}>
            {revoke.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Revoke relay
          </Button>
        ) : null
      }
    >
      <RelayDetails relay={relay} />
      {folders.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {relay.status === 'PENDING'
            ? 'Waiting for the relay to pair: run the downloaded program on that computer.'
            : 'Connected, no folders shared yet: share one with the relay program on that computer.'}
        </p>
      ) : (
        <div className="space-y-4">
          {folders.map((drive) => (
            <RelayFolderCard key={drive.id} drive={drive} isHubAdmin={isHubAdmin} />
          ))}
        </div>
      )}
      {isHubAdmin ? null : <AdminOnlyNote />}
      <ConfirmActionDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Revoke relay"
        description={`Revoke relay '${relay.name}'? Its folders are removed from this Hub and it cannot reconnect.`}
        confirmLabel="Revoke"
        onConfirm={handleRevoke}
      />
    </InstanceLayout>
  );
}

function XeroInstance({ id, memberId, isHubAdmin }: InstanceProps) {
  const connection = useXeroConnection(memberId);
  const startConnect = useStartXeroHubConnect();
  const leave = useLeave();

  if (connection.isLoading) {
    return <InstanceLoading />;
  }
  if (connection.isError) {
    return <InstanceError error={connection.error} />;
  }
  const row = rowsFromXeroConnection(connection.data).find((candidate) => candidate.id === id);
  if (!row) {
    return <InstanceGone />;
  }
  const revoked = connection.data?.connection?.status === 'REVOKED';

  return (
    <InstanceLayout
      kind="xero"
      row={row}
      actions={
        isHubAdmin && revoked ? (
          <Button
            type="button"
            onClick={() => startConnectInNewTab({ label: 'Xero', start: () => startConnect.mutateAsync() })}
            disabled={startConnect.isPending}
          >
            {startConnect.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Connect again
          </Button>
        ) : null
      }
    >
      <div className="space-y-4">
        <XeroConnectionBody memberId={memberId} isHubAdmin={isHubAdmin} onDisconnected={leave} />
      </div>
      {isHubAdmin ? null : <AdminOnlyNote />}
    </InstanceLayout>
  );
}

function McpInstance({ id, memberId, isHubAdmin }: InstanceProps) {
  const connections = useMcpServerConnections(memberId > 0);
  const pinMutation = usePinMcpServerConnection();
  const probeMutation = useProbeMcpServerConnection();
  const [probe, setProbe] = useState<McpProbeState | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const leave = useLeave();

  if (connections.isLoading) {
    return <InstanceLoading />;
  }
  if (connections.isError) {
    return <InstanceError error={connections.error} />;
  }
  const connection = (connections.data?.connections ?? []).find((item) => item.id === id);
  const row = connection ? rowsFromMcpConnections([connection])[0] : undefined;
  if (!connection || !row) {
    return <InstanceGone />;
  }

  const handlePin = () => {
    pinMutation.mutate(connection.id, {
      onSuccess: (pinned) => setProbe({ kind: 'ok', text: describePinResult(pinned) }),
      onError: (error) => setProbe({ kind: 'error', text: getAxiosErrorDisplayMessage(error, 'Pin failed.') }),
    });
  };

  const handleProbe = () => {
    probeMutation.mutate(connection.id, {
      onSuccess: (result) =>
        setProbe({
          kind: 'ok',
          text: `${result.toolCount} tool(s)${result.toolNames.length > 0 ? `: ${result.toolNames.join(', ')}` : ''}`,
        }),
      onError: (error) => setProbe({ kind: 'error', text: getAxiosErrorDisplayMessage(error, 'Probe failed.') }),
    });
  };

  return (
    <InstanceLayout
      kind="mcp"
      row={row}
      actions={
        isHubAdmin ? (
          <>
            <Button variant="ghost" size="sm" onClick={handlePin} disabled={pinMutation.isPending}>
              {pinMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Pin current tools'}
            </Button>
            <Button variant="ghost" size="sm" onClick={handleProbe} disabled={probeMutation.isPending}>
              {probeMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Probe'}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setEditOpen(true)}>
              Edit
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setDeleteOpen(true)}>
              Delete
            </Button>
          </>
        ) : null
      }
    >
      <McpConnectionCard connection={connection} probe={probe} />
      {isHubAdmin ? null : <AdminOnlyNote />}
      <McpConnectionFormDialog open={editOpen} onOpenChange={setEditOpen} connection={connection} />
      <McpConnectionDeleteDialog
        connection={connection}
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        onDeleted={leave}
      />
    </InstanceLayout>
  );
}

export function IntegrationInstancePage({ kind, id, memberId, isHubAdmin }: IntegrationInstancePageProps) {
  const props = { id, memberId, isHubAdmin };
  switch (kind) {
    case 'google-account':
    case 'microsoft-account':
      return <AccountInstance kind={kind} {...props} />;
    case 'google-drive':
      return <GoogleDriveInstance {...props} />;
    case 'microsoft-tenant':
      return <MicrosoftTenantInstance {...props} />;
    case 'relay':
      return <RelayInstance {...props} />;
    case 'xero':
      return <XeroInstance {...props} />;
    case 'mcp':
      return <McpInstance {...props} />;
  }
}
